import type { SxProps } from '@mui/material';
import { Button, CircularProgress, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import Box from '@mui/material/Box';
import type { FC, ReactNode } from 'react';
import { ConfirmationDialog, SelectLanguage, TextField } from 'components/common';
import type { CreativeTextDetails } from '../types';
import { isEmpty } from 'lodash-es';
import type { CreativeTextPayload, CreativeTextResponse } from '../api';
import { useAddCreativeText, useUpdateCreativeText } from '../api';
import { FormControlContainer } from './CreativeTextsComponents';
import { addCreativeTextFormValidator } from '../validation';
import type { z } from 'zod';
import DeleteCreativeText from './DeleteCreativeText';
import { useEnqueueSnackbar, useModal, useScrollToError } from 'hooks';
import { useNavigate } from 'react-router';
import { usePermissionGuard } from 'features/login';
import {
  createCabinetMutationTracking,
  trackCabinetSubmitAttempt,
} from 'lib/datadog';

interface CreativeTextFormProps {
  creativeText?: CreativeTextDetails;
  titleForm?: ReactNode;
  onSuccess?: (data: CreativeTextResponse) => void;
  sx?: SxProps;
}

type FormData = z.infer<typeof addCreativeTextFormValidator.schema>;

const CreativeTextForm: FC<CreativeTextFormProps> =
  ({
     creativeText,
     titleForm,
     onSuccess,
     sx,
   }) => {
    const { accessToDeleteOriginaltextcreative } = usePermissionGuard();
    const { onShowErrorAlert, onShowAlert } = useEnqueueSnackbar();
    const navigate = useNavigate();
    const { open, close, isOpen } = useModal();
    const isEditMode = !isEmpty(creativeText);

    const addCreativeText = useAddCreativeText({
      config: createCabinetMutationTracking<CreativeTextResponse>({
        platform: 'tiktok',
        entity: 'creative_text',
        mode: 'create',
        onSuccess: (data: CreativeTextResponse) => {
          onShowAlert('Creative text added successfully');
          if (onSuccess) {
            onSuccess(data);
          } else {
            navigate('/cabinet/tiktok/creative-texts');
          }
        },
        onError: (error) => {
          onShowErrorAlert(error.message);
        },
      }),
    });

    const updateCreativeText = useUpdateCreativeText({
      config: createCabinetMutationTracking({
        platform: 'tiktok',
        entity: 'creative_text',
        mode: 'edit',
        onSuccess: () => {
          onShowAlert('Creative text updated successfully');
          navigate('/cabinet/tiktok/creative-texts');
        },
        onError: (error) => {
          onShowErrorAlert(error.message);
        },
      }),
    });

    const formMethods = useForm<FormData>({
      resolver: addCreativeTextFormValidator.resolver,
      defaultValues: {
        text: creativeText?.text ?? '',
        language: creativeText?.language ?? '',
      },
    });

    const {
      handleSubmit,
      formState: { errors },
    } = formMethods;

    useScrollToError<FormData>({ errors });

    const onSubmit = (data: FormData) => {
      trackCabinetSubmitAttempt(
        'tiktok',
        'creative_text',
        isEditMode ? 'edit' : 'create',
      );

      const payload: CreativeTextPayload = {
        text: data.text,
        language: data.language,
      };

      if (isEditMode && creativeText) {
        updateCreativeText.mutate({ ...payload, id: creativeText.id });
      } else {
        addCreativeText.mutate(payload);
      }
    };

    const isPending = isEditMode
      ? updateCreativeText.isPending
      : addCreativeText.isPending;

    return (
      <>
        <FormProvider {...formMethods}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              pr: 2,
              alignItems: 'start',
              justifyContent: 'start',
              ...sx,
            }}
          >
            {titleForm ?? (
              <Typography variant={'subtitle1'}>
                {isEditMode ? 'Edit text' : 'Add text'}
              </Typography>
            )}
            <FormControlContainer title={'Text'}>
              <TextField
                multiline
                name={'text'}
                placeholder={'Value'}
                inputProps={{ sx: { minHeight: 240 } }}
              />
            </FormControlContainer>
            <FormControlContainer title={'Language'}>
              <Box sx={{
                width: '100%',
                maxWidth: '400px',
                display: 'flex',
                flexDirection: 'row',
              }}>
                <SelectLanguage name={'language'} />
              </Box>
            </FormControlContainer>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Button
                startIcon={
                  isPending && <CircularProgress color={'inherit'} size={18} />
                }
                onClick={handleSubmit(onSubmit)}
                variant={'contained'}
                disabled={isPending}
              >
                {isEditMode ? 'Update text' : 'Save text'}
              </Button>
              {isEditMode && creativeText && (
                <>
                  <Button
                    variant={'outlined'}
                    color={'error'}
                    onClick={open}
                    disabled={isPending || !accessToDeleteOriginaltextcreative}
                  >
                    Delete
                  </Button>
                  {accessToDeleteOriginaltextcreative && <DeleteCreativeText
                    isOpen={isOpen}
                    close={close}
                    id={creativeText.id}
                    name={creativeText.text}
                    open={open}
                  />}
                </>
              )}
            </Box>
          </Box>
          <ConfirmationDialog isUnblock={addCreativeText.isSuccess || updateCreativeText.isSuccess} />
        </FormProvider>
      </>
    );
  };

export default CreativeTextForm;
